import { isApiError } from '@/lib/api/errors'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { downloadFile, http } from '@/lib/api/client'
import type {
  Paginated,
  Resource,
  SupportEmail,
  TicketDetail,
  TicketList,
  TicketStatus,
} from '@/lib/api/types'

export type StatusFilter = TicketStatus | 'all'
export type AssignedFilter = 'all' | 'me' | 'unassigned'
export type SourceFilter = 'all' | 'app' | 'website' | 'email'

export function useTickets(
  status: StatusFilter,
  assigned: AssignedFilter,
  source: SourceFilter,
  search: string,
  page: number,
) {
  return useQuery({
    queryKey: ['tickets', status, assigned, source, search, page],
    queryFn: () => http.get<TicketList>('/tickets', { status, assigned, source, search, page }),
    placeholderData: keepPreviousData,
  })
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => (await http.get<Resource<TicketDetail>>(`/tickets/${id}`)).data,
  })
}

function useUpdate(id: string) {
  const queryClient = useQueryClient()
  return (ticket: TicketDetail) => {
    queryClient.setQueryData(['ticket', id], ticket)
    void queryClient.invalidateQueries({ queryKey: ['tickets'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }
}

export function useReply(id: string) {
  const queryClient = useQueryClient()
  const update = useUpdate(id)
  return useMutation({
    mutationFn: async (body: string) =>
      (await http.post<Resource<TicketDetail>>(`/tickets/${id}/messages`, { body })).data,
    onSuccess: update,
    onError: (error) => {
      if (isApiError(error) && error.code === 'ticket_resolved')
        void queryClient.invalidateQueries({ queryKey: ['ticket', id] })
    },
  })
}

export function useAssign(id: string) {
  const update = useUpdate(id)
  return useMutation({
    mutationFn: async (staffId: string | null) =>
      (await http.post<Resource<TicketDetail>>(`/tickets/${id}/assign`, { staff_id: staffId })).data,
    onSuccess: update,
  })
}

export function useSetStatus(id: string) {
  const update = useUpdate(id)
  return useMutation({
    mutationFn: async (status: 'resolved' | 'open') =>
      (await http.post<Resource<TicketDetail>>(`/tickets/${id}/status`, { status })).data,
    onSuccess: update,
  })
}

/** A file from a customer's email, downloaded with the staff session (never opened in the browser). */
export const downloadAttachment = (ticketId: string, attachmentId: string) =>
  downloadFile(`/tickets/${ticketId}/attachments/${attachmentId}`)

export type HeldFilter = 'all' | 'held' | 'skipped' | 'failed' | 'dismissed'

/** Emails to support that didn't become tickets: spam, automatic mail, unreadable. */
export function useSupportEmails(status: HeldFilter, page: number) {
  return useQuery({
    queryKey: ['support-emails', status, page],
    queryFn: () => http.get<Paginated<SupportEmail>>('/support-emails', { status, page }),
    placeholderData: keepPreviousData,
  })
}

function useEmailAction(action: 'ticket' | 'dismiss') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await http.post<Resource<SupportEmail>>(`/support-emails/${id}/${action}`)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['support-emails'] })
      void queryClient.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

/** Make a ticket from a held or skipped email anyway. */
export const useMakeTicket = () => useEmailAction('ticket')

export const useDismissEmail = () => useEmailAction('dismiss')

export function useFollowUp(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (values: { subject: string; body: string }) =>
      (await http.post<Resource<TicketDetail>>(`/tickets/${id}/follow-up`, values)).data,
    onSuccess: (ticket) => {
      queryClient.setQueryData(['ticket', ticket.id], ticket)
      void queryClient.invalidateQueries({ queryKey: ['tickets'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['ticket', id] })
    },
  })
}
